// One booking question (state/booking-questions-form.ts), as the checkout
// asks it. The label stays a plain <label for> — its accessible name is the
// question and the required mark, exactly as before ("Rider name *") — and the
// help line sits under it, tied to the control with aria-describedby. Choices
// (multi-select, yes/no) are a fieldset whose legend is the question.
//
// A sensitive question points at the privacy line printed once above its run
// ("Only Demo's managers see these answers.", ParticipantDetails). One whose
// answer the server may already hold but never returns, left empty, reads
// "Kept private — type to change." instead of demanding an answer; an
// optional one also offers "Clear" (the save then sends an explicit null).
// For a signed-in member below manager the private questions are shown
// disabled, under the staff line ("Medical and guardian details: managers
// only", or "Questions no longer asked: managers only" over a required
// question the service has since deleted): they cannot answer them and
// nothing is sent.
//
// Colours: only the theme tokens (--vt-accent for the ticks); the rest is in
// styles.css under .vt-q.

import { wideOptions, type QuestionField } from "@vertu/shared";
import { toggleChoice, type DraftValue } from "../../state/booking-questions-form";

export function QuestionControl({
  field,
  id,
  value,
  onChange,
  privateNoteId,
  savedNote,
  noteAction,
  disabled = false,
}: {
  field: QuestionField;
  /** Unique per form, guest and question. */
  id: string;
  value: DraftValue;
  onChange: (value: DraftValue) => void;
  /** The privacy line of this sensitive question's run. */
  privateNoteId?: string;
  /** Set while the answer is kept on the server and this box is empty: not required. */
  savedNote?: string | null;
  /** A button after the note ("Clear" a kept optional answer, "Undo" a clearing). */
  noteAction?: { label: string; ariaLabel: string; onClick: () => void } | null;
  /** Shown but not answerable (a private question, for staff below manager). */
  disabled?: boolean;
}) {
  const required = field.required && !savedNote && !disabled;
  // A no-break space before the asterisk: a label that wraps in a narrow
  // column ("Estimated weight *") never leaves the mark alone on a line.
  const mark = disabled ? "" : field.required && !savedNote ? "\u00a0*" : field.required ? "" : " (optional)";
  const helpId = field.help ? `${id}-help` : undefined;
  const savedId = savedNote ? `${id}-saved` : undefined;
  const describedBy = [helpId, savedId, privateNoteId].filter(Boolean).join(" ") || undefined;
  const notes = (
    <>
      {field.help && (
        <p id={helpId} className="vt-q-help">
          {field.help}
        </p>
      )}
      {savedNote && (
        <p id={savedId} className="vt-q-help vt-q-saved">
          {savedNote}
          {noteAction && (
            <>
              {" "}
              <button
                type="button"
                className="vt-q-note-action"
                aria-label={noteAction.ariaLabel}
                onClick={noteAction.onClick}
              >
                {noteAction.label}
              </button>
            </>
          )}
        </p>
      )}
    </>
  );
  const text = typeof value === "string" || typeof value === "number" ? String(value) : "";

  if (field.type === "multiselect") {
    const chosen = Array.isArray(value) ? value : [];
    const wide = wideOptions(field.options ?? []);
    return (
      <fieldset className="vt-q vt-q-group" aria-describedby={describedBy}>
        <legend className="vt-q-label">
          {field.label}
          {mark}
        </legend>
        {notes}
        {/* The options flow into columns (.vt-q-options): several to a row
            where they fit, in their order along each row; the list's last
            options, when long, take the whole width (wideOptions). */}
        <div className="vt-q-options">
          {(field.options ?? []).map((option, i) => (
            <label
              key={option}
              className={wide[i] ? "vt-q-choice vt-q-choice-wide" : "vt-q-choice"}
              htmlFor={`${id}-${i}`}
            >
              <input
                id={`${id}-${i}`}
                type="checkbox"
                checked={chosen.includes(option)}
                disabled={disabled}
                onChange={(event) => onChange(toggleChoice(field, value, option, event.target.checked))}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  }

  if (field.type === "yesno") {
    return (
      <fieldset className="vt-q vt-q-group vt-q-yesno" aria-describedby={describedBy}>
        <legend className="vt-q-label">
          {field.label}
          {mark}
        </legend>
        {notes}
        {([true, false] as const).map((answer) => (
          <label key={String(answer)} className="vt-q-choice" htmlFor={`${id}-${answer ? "yes" : "no"}`}>
            <input
              id={`${id}-${answer ? "yes" : "no"}`}
              type="radio"
              name={id}
              checked={value === answer}
              disabled={disabled}
              onChange={() => onChange(answer)}
            />
            <span>{answer ? "Yes" : "No"}</span>
          </label>
        ))}
      </fieldset>
    );
  }

  if (field.type === "checkbox") {
    return (
      <div className="vt-q vt-q-tick">
        <label className="vt-q-choice" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            disabled={disabled}
            aria-describedby={describedBy}
            onChange={(event) => onChange(event.target.checked ? true : null)}
          />
          <span>
            {field.label}
            {mark}
          </span>
        </label>
        {notes}
      </div>
    );
  }

  return (
    <div className="vt-q">
      <label className="vt-q-label" htmlFor={id}>
        {field.label}
        {mark}
      </label>
      {notes}
      {field.type === "select" ? (
        <select
          id={id}
          className="vt-q-input"
          value={text}
          required={required}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Choose…</option>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : field.type === "longtext" ? (
        <textarea
          id={id}
          className="vt-q-input"
          rows={3}
          value={text}
          maxLength={2000}
          required={required}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={id}
          className="vt-q-input"
          type={
            field.type === "number"
              ? "number"
              : field.format === "email"
                ? "email"
                : field.format === "phone"
                  ? "tel"
                  : "text"
          }
          inputMode={field.type === "number" ? "decimal" : undefined}
          value={text}
          min={field.type === "number" ? (field.min ?? 0) : undefined}
          max={field.type === "number" ? (field.max ?? 1000000) : undefined}
          step={field.type === "number" ? "any" : undefined}
          maxLength={field.type === "number" ? undefined : 500}
          required={required}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}
